import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Zap, Download, BookOpen, Sparkles } from 'lucide-react';
import { TierSection } from '../components/create-event/TierSection';
import { PaymentLinksSection } from '../components/create-event/PaymentLinksSection';
import { BrandingSection, StaffAccessSection } from '../components/create-event/BrandingStaffSections';
import SmartEntryFlowSection from '../components/create-event/SmartEntryFlowSection';
import SourceOptionsSection from '../components/create-event/SourceOptionsSection';
import ValidationSummary from '../components/event-form/ValidationSummary';
import {
  resolveEventStartDateTime,
  resolveEventEndDateTime,
  toLocalInput,
} from '../lib/eventDateTime';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYMENT_METHODS = ['Venmo', 'Zelle', 'PayPal', 'Cash App', 'Stripe', 'Other'];

export default function CreateEvent({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [useDefaults, setUseDefaults] = useState(false);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [savedPassTiers, setSavedPassTiers] = useState([]);
  const [savedHostDefaults, setSavedHostDefaults] = useState({});
  const [form, setForm] = useState({
    title: '', description: '', event_type: '', date: '', time: '', end_time: '', end_date: '', is_multi_day: false, location: '',
    capacity: '', convenience_fee: 0, release_method: 'automatic', notification_schedule: 'instant', is_unlimited_capacity: false,
    payment_links: [], pass_tiers: [], organizer_logo_url: null, event_image_url: null,
    access_start_datetime: '', access_end_datetime: '',
    entry_mode: 'standard', entry_flow: null,
    source_options: null,
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingOrgLogo, setSavingOrgLogo] = useState(false);
  const [showTierImport, setShowTierImport] = useState(false);
  const [tierImportSelection, setTierImportSelection] = useState({});
  const [showPaymentImport, setShowPaymentImport] = useState(false);
  const [paymentImportSelection, setPaymentImportSelection] = useState({});
  const [staffEmail, setStaffEmail] = useState('');
  const [addingStaff, setAddingStaff] = useState(false);
  const [savingPaymentDefault, setSavingPaymentDefault] = useState(null);

  // Tracks whether the host-default Staff Access Window offsets have already
  // been auto-applied this session. Prevents re-applying after the host
  // manually clears / edits the window.
  const accessWindowAutoAppliedRef = useRef({ pre: false, post: false });
  // Set true the moment the user manually changes either access-window input.
  // Once true, the reactive auto-apply effect short-circuits for the rest
  // of the session, preventing any ghost writes over user intent.
  const hasManuallyEditedStaffWindowRef = useRef(false);

  useEffect(() => { fetchHostDefaults(); }, []);

  const fetchHostDefaults = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [paymentRes, tiersRes, logoRes, defaultsRes] = await Promise.all([
        axios.get(`${API}/users/payment-methods`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/pass-tiers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/organizer-logo`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { organizer_logo_url: null } })),
        axios.get(`${API}/users/host-defaults`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setSavedPaymentMethods(paymentRes.data);
      setSavedPassTiers(tiersRes.data);
      setSavedHostDefaults(defaultsRes.data);
      if (logoRes.data.organizer_logo_url) {
        setForm(prev => ({ ...prev, organizer_logo_url: prev.organizer_logo_url || logoRes.data.organizer_logo_url }));
      }
    } catch {}
  };

  const handleUseDefaults = (checked) => {
    setUseDefaults(checked);
    if (checked) {
      const paymentLinks = savedPaymentMethods.map((pm, i) => ({ id: `pl_${Date.now()}_${i}`, method: pm.method, payment_method_type: pm.payment_method_type || 'link', payment_provider: pm.payment_provider, handle: pm.handle, url: pm.url || '', is_auto_generated: pm.is_auto_generated || false, recipient_name: pm.recipient_name || '', email_or_phone: pm.email_or_phone || '', qr_code_image: pm.qr_code_image || '', suggested_memo: pm.suggested_memo || '', instructions: pm.instructions || '', _from_saved: true }));
      const passTiers = savedPassTiers.map((tier, i) => ({ ...tier, id: `tier_${Date.now()}_${i}` }));
      const defaultStaff = (savedHostDefaults.check_in_staff || []).map(s => ({ ...s }));
      setForm(prev => {
        const next = {
          ...prev,
          payment_links: paymentLinks,
          pass_tiers: passTiers,
          release_method: savedHostDefaults.release_method || prev.release_method,
          convenience_fee: 0,
          notification_schedule: savedHostDefaults.notification_schedule || prev.notification_schedule,
          source_options: Array.isArray(savedHostDefaults.source_options) && savedHostDefaults.source_options.length > 0
            ? savedHostDefaults.source_options
            : prev.source_options,
        };
        // Apply access-window host defaults inline IF the corresponding fields
        // are currently empty AND date/time are available. Otherwise, the
        // reactive useEffect below will catch it once date/time are set.
        // Note: refs are mutated AFTER setForm (below) to stay pure inside
        // the updater (dev-mode StrictMode invokes updaters twice).
        const preMin = savedHostDefaults.staff_access_pre_start_minutes;
        const postMin = savedHostDefaults.staff_access_post_end_minutes;
        const startDt = resolveEventStartDateTime({ date: prev.date, time: prev.time });
        const endDt = resolveEventEndDateTime({
          date: prev.date,
          time: prev.time,
          end_time: prev.end_time,
          end_date: prev.end_date,
          is_multi_day: prev.is_multi_day,
        });
        if (preMin != null && !prev.access_start_datetime && startDt) {
          next.access_start_datetime = toLocalInput(new Date(startDt.getTime() - preMin * 60 * 1000));
        }
        if (postMin != null && !prev.access_end_datetime && endDt) {
          next.access_end_datetime = toLocalInput(new Date(endDt.getTime() + postMin * 60 * 1000));
        }
        return next;
      });
      // Mark refs AFTER setForm based on whether we likely applied. Any race
      // with the reactive effect is still safe because its guard reads
      // form.access_start_datetime / access_end_datetime which now reflect
      // the inline write.
      const preMin = savedHostDefaults.staff_access_pre_start_minutes;
      const postMin = savedHostDefaults.staff_access_post_end_minutes;
      const startDtNow = resolveEventStartDateTime({ date: form.date, time: form.time });
      const endDtNow = resolveEventEndDateTime({
        date: form.date,
        time: form.time,
        end_time: form.end_time,
        end_date: form.end_date,
        is_multi_day: form.is_multi_day,
      });
      if (preMin != null && !form.access_start_datetime && startDtNow) {
        accessWindowAutoAppliedRef.current.pre = true;
      }
      if (postMin != null && !form.access_end_datetime && endDtNow) {
        accessWindowAutoAppliedRef.current.post = true;
      }
      // Load default staff into event staff (will be sent on submit)
      if (defaultStaff.length > 0) {
        setForm(prev => ({ ...prev, staff: defaultStaff }));
      }
      const loaded = [paymentLinks.length > 0 && `${paymentLinks.length} payment method(s)`, passTiers.length > 0 && `${passTiers.length} tier(s)`, defaultStaff.length > 0 && `${defaultStaff.length} staff`, savedHostDefaults.release_method && 'event settings'].filter(Boolean);
      if (loaded.length > 0) toast.success(`Loaded: ${loaded.join(', ')}`);
      else toast.info('No saved defaults found. Set them up in Settings.');
    } else {
      // Toggling off — re-arm auto-apply so re-enabling re-applies if the
      // window is still empty.
      accessWindowAutoAppliedRef.current = { pre: false, post: false };
    }
  };

  // Reactive auto-apply: if the host has configured staff-access offset
  // defaults and the event date/time becomes available, auto-populate the
  // access window. Runs independently of the Quick Setup toggle — the user
  // opts in by clicking "Save as default" in the Staff Access Window. Never
  // overwrites a value the host has set manually (tracked via the ref below).
  useEffect(() => {
    const preMin = savedHostDefaults?.staff_access_pre_start_minutes;
    const postMin = savedHostDefaults?.staff_access_post_end_minutes;
    if (preMin == null && postMin == null) return;
    if (hasManuallyEditedStaffWindowRef.current) return;

    const startDt = resolveEventStartDateTime({ date: form.date, time: form.time });
    const endDt = resolveEventEndDateTime({
      date: form.date,
      time: form.time,
      end_time: form.end_time,
      end_date: form.end_date,
      is_multi_day: form.is_multi_day,
    });

    const shouldApplyPre =
      preMin != null &&
      !form.access_start_datetime &&
      !accessWindowAutoAppliedRef.current.pre &&
      !!startDt;
    const shouldApplyPost =
      postMin != null &&
      !form.access_end_datetime &&
      !accessWindowAutoAppliedRef.current.post &&
      !!endDt;

    if (!shouldApplyPre && !shouldApplyPost) return;

    const updates = {};
    if (shouldApplyPre) {
      updates.access_start_datetime = toLocalInput(new Date(startDt.getTime() - preMin * 60 * 1000));
      accessWindowAutoAppliedRef.current.pre = true;
    }
    if (shouldApplyPost) {
      updates.access_end_datetime = toLocalInput(new Date(endDt.getTime() + postMin * 60 * 1000));
      accessWindowAutoAppliedRef.current.post = true;
    }
    setForm(prev => ({ ...prev, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedHostDefaults, form.date, form.time, form.end_time, form.end_date, form.is_multi_day]);

  // --- Staff Access Window: manual-edit guard + save-as-default handler ---
  // Proxy setForm for StaffAccessSection only. Any update that changes
  // access_start_datetime or access_end_datetime flips the manual-edit flag
  // so the reactive auto-apply effect stops writing for the rest of the
  // session. Auto-apply uses plain `setForm`, not this wrapper.
  const staffSectionSetForm = (updater) => {
    setForm(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.access_start_datetime !== prev.access_start_datetime ||
          next.access_end_datetime !== prev.access_end_datetime) {
        hasManuallyEditedStaffWindowRef.current = true;
      }
      return next;
    });
  };

  const handleSaveStaffDefault = async ({ pre_min, post_min }) => {
    // Clamp to ±1440 as a final safety net — the UI already disables the
    // button out of range, but this keeps the PUT payload strictly valid.
    const clamp = (v) => v == null ? null : Math.max(-1440, Math.min(1440, v));
    try {
      await axios.put(`${API}/users/host-defaults`, {
        release_method: savedHostDefaults.release_method || 'automatic',
        convenience_fee: savedHostDefaults.convenience_fee || 0,
        notification_schedule: savedHostDefaults.notification_schedule || 'instant',
        staff_access_pre_start_minutes: clamp(pre_min),
        staff_access_post_end_minutes: clamp(post_min),
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });
      setSavedHostDefaults(prev => ({
        ...prev,
        staff_access_pre_start_minutes: clamp(pre_min),
        staff_access_post_end_minutes: clamp(post_min),
      }));
      toast.success('Default saved');
    } catch {
      toast.error('Could not save default');
    }
  };

  // --- Tier Import helpers ---
  const isTierAlreadyImported = (savedTier) => {
    return form.pass_tiers.some(t => t.tier_name === savedTier.tier_name && t.type === savedTier.type);
  };

  const openTierImport = () => {
    const sel = {};
    savedPassTiers.forEach(t => { sel[t.id] = !isTierAlreadyImported(t); });
    setTierImportSelection(sel);
    setShowTierImport(true);
  };

  const importSelectedTiers = () => {
    const toImport = savedPassTiers.filter(t => tierImportSelection[t.id] && !isTierAlreadyImported(t));
    if (toImport.length === 0) { toast.info('No new templates to import'); setShowTierImport(false); return; }
    const newTiers = toImport.map((tier, i) => ({ ...tier, id: `tier_${Date.now()}_${i}` }));
    setForm(prev => ({ ...prev, pass_tiers: [...prev.pass_tiers, ...newTiers] }));
    toast.success(`Imported ${newTiers.length} tier template${newTiers.length !== 1 ? 's' : ''}`);
    setShowTierImport(false);
  };

  // --- Payment Import helpers ---
  const isPaymentAlreadyImported = (savedPm) => {
    return form.payment_links.some(p => p.method === savedPm.method && ((savedPm.payment_method_type || 'link') === 'link' ? p.url === savedPm.url : p.email_or_phone === savedPm.email_or_phone));
  };

  const openPaymentImport = () => {
    const sel = {};
    savedPaymentMethods.forEach(pm => { sel[pm.id] = !isPaymentAlreadyImported(pm); });
    setPaymentImportSelection(sel);
    setShowPaymentImport(true);
  };

  const importSelectedPayments = () => {
    const toImport = savedPaymentMethods.filter(pm => paymentImportSelection[pm.id] && !isPaymentAlreadyImported(pm));
    if (toImport.length === 0) { toast.info('No new methods to import'); setShowPaymentImport(false); return; }
    const newLinks = toImport.map((pm, i) => ({ id: `pl_${Date.now()}_${i}`, method: pm.method, payment_method_type: pm.payment_method_type || 'link', url: pm.url || '', recipient_name: pm.recipient_name || '', email_or_phone: pm.email_or_phone || '', qr_code_image: pm.qr_code_image || '', suggested_memo: pm.suggested_memo || '', instructions: pm.instructions || '', _from_saved: true }));
    setForm(prev => ({ ...prev, payment_links: [...prev.payment_links, ...newLinks] }));
    toast.success(`Imported ${newLinks.length} payment method${newLinks.length !== 1 ? 's' : ''}`);
    setShowPaymentImport(false);
  };

  const addPassTier = (type = 'entry') => setForm(prev => ({
    ...prev,
    pass_tiers: [...prev.pass_tiers, {
      id: `tier_${Date.now()}`, tier_name: '', price: 0, quantity_available: 0,
      is_unlimited: false,
      attendees_per_pass: type === 'entry' ? 1 : 1, description: '', is_comp: false,
      status_default: 'active', requires_access_code: false, access_code_hash: '',
      type: type, display_order: prev.pass_tiers.length,
      allow_quantity_selection: true, min_quantity: '', max_quantity: ''
    }]
  }));

  const updatePassTier = (index, field, value) => {
    const updated = [...form.pass_tiers];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'is_comp' && value) updated[index].price = 0;
    if (field === 'type' && value === 'addon') {
      updated[index].requires_access_code = false;
      updated[index].access_code_hash = '';
    }
    setForm({ ...form, pass_tiers: updated });
  };

  const removePassTier = (index) => setForm({ ...form, pass_tiers: form.pass_tiers.filter((_, i) => i !== index) });

  const addPaymentLink = () => setForm({ ...form, payment_links: [...form.payment_links, { id: `pl_${Date.now()}`, method: '', payment_method_type: 'link', url: '', recipient_name: '', email_or_phone: '', qr_code_image: '', suggested_memo: '', instructions: '' }] });

  const updatePaymentLink = (index, field, value) => {
    const updated = [...form.payment_links];
    updated[index][field] = value;
    setForm({ ...form, payment_links: updated });
  };

  const removePaymentLink = (index) => setForm({ ...form, payment_links: form.payment_links.filter((_, i) => i !== index) });

  const handleFileUpload = async (file, type) => {
    const setFn = type === 'logo' ? setUploadingLogo : setUploadingImage;
    setFn(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/upload`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, [type === 'logo' ? 'organizer_logo_url' : 'event_image_url']: response.data.url }));
      toast.success(`${type === 'logo' ? 'Organizer Logo' : 'Event Flyer'} uploaded`);
    } catch { toast.error(`Failed to upload`); }
    finally { setFn(false); }
  };

  const handleSaveOrganizerLogoDefault = async () => {
    if (!form.organizer_logo_url) { toast.error('Upload an organizer logo first'); return; }
    setSavingOrgLogo(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/settings/organizer-logo-url`, { organizer_logo_url: form.organizer_logo_url }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success('Organizer logo saved as default for future events');
    } catch { toast.error('Failed to save default'); }
    finally { setSavingOrgLogo(false); }
  };

  const handleSavePaymentMethodDefault = async (index) => {
    const link = form.payment_links[index];
    if (!link?.method) { toast.error('Select a payment method first'); return; }
    setSavingPaymentDefault(index);
    try {
      const token = localStorage.getItem('session_token');
      const existing = savedPaymentMethods.find(pm => pm.method === link.method && pm.payment_method_type === link.payment_method_type);
      if (existing) {
        await axios.put(`${API}/users/payment-methods/${existing.id}`, { ...existing, qr_code_image: link.qr_code_image || existing.qr_code_image, recipient_name: link.recipient_name || existing.recipient_name, email_or_phone: link.email_or_phone || existing.email_or_phone, suggested_memo: link.suggested_memo || existing.suggested_memo, instructions: link.instructions || existing.instructions }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const newPm = { id: `pm_${Date.now()}`, method: link.method, payment_method_type: link.payment_method_type, recipient_name: link.recipient_name || '', email_or_phone: link.email_or_phone || '', qr_code_image: link.qr_code_image || '', suggested_memo: link.suggested_memo || '', instructions: link.instructions || '', url: link.url || '', is_default: false };
        await axios.post(`${API}/users/payment-methods`, newPm, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(`${link.method} saved as default for future events`);
      // Refresh saved methods
      const res = await axios.get(`${API}/users/payment-methods`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedPaymentMethods(res.data);
    } catch { toast.error('Failed to save payment default'); }
    finally { setSavingPaymentDefault(null); }
  };

  const handlePaymentQRUpload = async (file, index) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/upload`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      updatePaymentLink(index, 'qr_code_image', response.data.url);
      toast.success('QR code uploaded');
    } catch { toast.error('Failed to upload QR code'); }
  };

  // Only count entry tiers for capacity summary
  const entryTiers = form.pass_tiers.filter(t => (t.type || 'entry') === 'entry');
  const addonTiers = form.pass_tiers.filter(t => t.type === 'addon');
  const tierCapacitySummary = entryTiers.reduce((acc, t) => { const q = parseInt(t.quantity_available) || 0; const p = parseInt(t.attendees_per_pass) || 1; if (q > 0) { acc.totalPasses += q; acc.totalAttendees += q * p; } return acc; }, { totalPasses: 0, totalAttendees: 0 });

  const invalidToastShownRef = React.useRef(false);
  const formRef = React.useRef(null);
  const [validationNonce, setValidationNonce] = React.useState(0);

  const handleSubmit = async (e) => {
    invalidToastShownRef.current = false;
    e.preventDefault();
    if ((form.payment_links || []).length === 0) { toast.error('Add at least one payment method'); return; }
    if (!form.is_unlimited_capacity && (!form.capacity || parseInt(form.capacity) < 1)) { toast.error('Set attendee capacity or enable unlimited'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        ...form,
        capacity: form.is_unlimited_capacity ? 0 : parseInt(form.capacity),
        is_unlimited_capacity: form.is_unlimited_capacity,
        convenience_fee: parseFloat(form.convenience_fee),
        // Smart Flow: only send when explicitly enabled (else null preserves legacy behavior)
        entry_mode: form.entry_mode === 'smart_flow' ? 'smart_flow' : null,
        entry_flow: form.entry_mode === 'smart_flow' ? (form.entry_flow || null) : null,
        payment_links: form.payment_links.map(({ _from_saved, ...link }) => link),
        pass_tiers: form.pass_tiers.map((t, i) => ({
          ...t,
          price: parseFloat(t.price) || 0,
          quantity_available: parseInt(t.quantity_available) || 0,
          attendees_per_pass: t.type === 'addon' ? 1 : (parseInt(t.attendees_per_pass) || 1),
          display_order: i,
          allow_quantity_selection: t.allow_quantity_selection !== false,
          min_quantity: t.min_quantity ? parseInt(t.min_quantity) : null,
          max_quantity: t.max_quantity ? parseInt(t.max_quantity) : null
        }))
      };
      const response = await axios.post(`${API}/events`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event created');
      const { auto_staff_assigned, auto_staff_skipped } = response.data;
      if (auto_staff_assigned > 0) {
        toast.success(`${auto_staff_assigned} default door team member${auto_staff_assigned > 1 ? 's' : ''} auto-assigned`);
      }
      if (auto_staff_skipped > 0) {
        toast.warning(`${auto_staff_skipped} default door team member${auto_staff_skipped > 1 ? 's were' : ' was'} skipped because they do not have accounts yet.`);
      }
      navigate(`/events/${response.data.event_id}/manage`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create event'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" className="-ml-2 text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
            <Button onClick={() => navigate('/guide')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-xs h-8" data-testid="guide-nav-button"><BookOpen className="w-3.5 h-3.5 mr-1.5" />VelvetGate Guide</Button>
          </div>
          <h1 className="text-3xl font-bold text-[#EAE7E1] tracking-tight">Create Event</h1>
          <p className="mt-1 text-base text-[#AFAFAF]">Fill in the details for your event</p>
          <div className="mt-4">
            <Button
              type="button"
              onClick={() => navigate('/create-event/guided')}
              variant="outline"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F2D27A] rounded-sm h-9 text-xs font-semibold"
              data-testid="try-guided-builder-button"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Try Guided Builder
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="space-y-8"
          data-testid="create-event-form"
          onInvalid={(e) => {
            setValidationNonce((n) => n + 1);
            if (invalidToastShownRef.current) return;
            invalidToastShownRef.current = true;
            const first = e.target;
            if (first && typeof first.scrollIntoView === 'function') {
              first.scrollIntoView({ behavior: 'smooth', block: 'center' });
              try { first.focus({ preventScroll: true }); } catch (_) {}
            }
            const lookupLabel = (el) => {
              if (!el) return 'required field';
              if (el.labels && el.labels[0]?.innerText) return el.labels[0].innerText;
              const prev = el.closest('div')?.querySelector('label');
              if (prev?.innerText) return prev.innerText;
              return el.name || el.getAttribute?.('data-testid') || 'required field';
            };
            const label = lookupLabel(first);
            toast.error(`Please fill in: ${String(label).replace(/\s*\*$/, '').trim()}`);
            setTimeout(() => { invalidToastShownRef.current = false; }, 1500);
          }}
        >
          {/* Quick Setup Toggle */}
          {(savedPaymentMethods.length > 0 || savedPassTiers.length > 0 || savedHostDefaults.release_method) ? (
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-sm p-5 flex items-center justify-between" data-testid="quick-setup-section">
              <div className="flex items-center space-x-3">
                <Zap className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <div className="font-semibold text-[#EAE7E1]">Quick Event Setup</div>
                  <div className="text-sm text-[#AFAFAF]">Auto-fill from your saved defaults</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={useDefaults} onChange={(e) => handleUseDefaults(e.target.checked)} className="sr-only peer" data-testid="use-defaults-toggle" />
                <div className="w-11 h-6 bg-[#1A1A1A] peer-focus:ring-2 peer-focus:ring-[#D4AF37]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-[#050505] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[#AFAFAF] after:border-[#1A1A1A] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4AF37]"></div>
              </label>
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-dashed border-[#1A1A1A] rounded-sm p-5 flex items-center justify-between" data-testid="no-defaults-hint">
              <div className="flex items-center space-x-3">
                <Zap className="w-5 h-5 text-[#555]" />
                <div>
                  <div className="font-semibold text-[#EAE7E1]">Quick Event Setup</div>
                  <div className="text-sm text-[#777]">Save your common setup once so future events take minutes.</div>
                </div>
              </div>
              <button onClick={() => navigate('/setup')} className="text-xs font-semibold text-[#C6A66B] hover:text-[#D4AF37] whitespace-nowrap" data-testid="go-setup-button">
                Set Up Defaults
              </button>
            </div>
          )}

          {/* Basic Info */}
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-6" data-testid="basic-info-section">
            <h2 className="text-xl font-semibold text-[#EAE7E1] mb-6 tracking-tight">Basic Information</h2>
            <div className="space-y-4">
              <div><Label>Event Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1" data-testid="event-title-input" /></div>
              <div><Label>Description *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={4} className="mt-1" data-testid="event-description-input" /></div>
              <div><Label>Event Type *</Label><Input value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} placeholder="e.g., Concert, Party, Conference" required className="mt-1" data-testid="event-type-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="mt-1" data-testid="event-date-input" /></div>
                <div><Label>Start Time *</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required className="mt-1" data-testid="event-time-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>End Time</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" data-testid="event-end-time-input" />{form.time && form.end_time && form.end_time < form.time && !form.is_multi_day && <p className="text-[10px] text-[#D4AF37] mt-1">Overnight — ends next day</p>}</div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 h-9 text-sm text-[#AFAFAF] cursor-pointer">
                    <input type="checkbox" checked={form.is_multi_day} onChange={e => setForm({ ...form, is_multi_day: e.target.checked, end_date: e.target.checked ? form.end_date : '' })} className="rounded" data-testid="multi-day-toggle" />
                    Multi-day event
                  </label>
                </div>
              </div>
              {form.is_multi_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} min={form.date} className="mt-1" data-testid="event-end-date-input" /></div>
                  <div />
                </div>
              )}
              <div><Label>Location *</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required className="mt-1" data-testid="event-location-input" /></div>

              {/* Location Visibility */}
              <div className="border border-[#1A1A1A] rounded-sm p-4">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[#EAE7E1] text-sm font-medium">Location Visibility</Label>
                  <button type="button" onClick={() => navigate('/guide#hidden-address')} className="text-[11px] text-[#D4AF37] hover:underline" data-testid="help-location-visibility">What does this mean?</button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="location-visibility-selector">
                  {[
                    { value: 'public', label: 'Public', desc: 'Shown everywhere' },
                    { value: 'hidden', label: 'Hidden', desc: 'Never shown' },
                    { value: 'revealed_later', label: 'Reveal Later', desc: 'Sent before event' },
                  ].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setForm(prev => ({ ...prev, location_visibility: opt.value }))}
                      className={`p-3 rounded-sm border-2 text-center transition-all ${form.location_visibility === opt.value ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#1A1A1A] hover:border-[#333]'}`}
                      data-testid={`loc-vis-${opt.value}`}>
                      <div className={`text-sm font-semibold ${form.location_visibility === opt.value ? 'text-[#EAE7E1]' : 'text-[#777]'}`}>{opt.label}</div>
                      <div className="text-[10px] text-[#555] mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>

                {/* Revealed Later options */}
                {form.location_visibility === 'revealed_later' && (
                  <div className="mt-4 space-y-3 border-t border-[#1A1A1A] pt-4">
                    <div>
                      <Label className="text-[#EAE7E1] text-sm">Private Location Details *</Label>
                      <p className="text-xs text-[#777] mb-1">This address is only sent via message — never shown publicly.</p>
                      <Textarea
                        value={form.private_location_details || ''}
                        onChange={e => setForm(prev => ({ ...prev, private_location_details: e.target.value }))}
                        placeholder="Full address, suite, gate code, parking instructions..."
                        rows={2}
                        className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]"
                        data-testid="private-location-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#EAE7E1] text-sm">Reveal Timing</Label>
                      <select
                        value={form.location_reveal_mode || 'manual'}
                        onChange={e => setForm(prev => ({ ...prev, location_reveal_mode: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-[#EAE7E1] focus:border-[#D4AF37]"
                        data-testid="reveal-mode-select"
                      >
                        <option value="manual">Send manually</option>
                        <option value="auto_48h">48 hours before event</option>
                        <option value="auto_36h">36 hours before event</option>
                        <option value="auto_24h">24 hours before event</option>
                      </select>
                      <p className="text-xs text-[#C6A66B] mt-1.5">
                        {form.location_reveal_mode !== 'manual'
                          ? 'Guests will receive the address automatically at the selected time.'
                          : 'You can send the location manually from the event management page.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>Total Attendee Capacity</Label>
                <div className="flex items-center gap-3 mt-1">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="unlimited-capacity-toggle">
                    <input type="checkbox" checked={form.is_unlimited_capacity} onChange={(e) => setForm({ ...form, is_unlimited_capacity: e.target.checked })} className="rounded border-[#1A1A1A]" />
                    <span className="text-sm text-[#AFAFAF]">Unlimited</span>
                  </label>
                  {!form.is_unlimited_capacity && (
                    <Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} required min="1" className="flex-1" placeholder="Max attendees" data-testid="event-capacity-input" />
                  )}
                </div>
                {!form.is_unlimited_capacity && <p className="text-sm text-[#777] mt-1">Maximum attendees. Couple passes count as 2, etc. Add-ons do NOT affect capacity.</p>}
                {!form.is_unlimited_capacity && tierCapacitySummary.totalPasses > 0 && <p className="text-sm text-[#D4AF37] mt-1">Entry tiers account for up to {tierCapacitySummary.totalAttendees} attendees across {tierCapacitySummary.totalPasses} passes.</p>}
                {form.is_unlimited_capacity && <p className="text-sm text-[#777] mt-1">Tier and add-on inventory limits still apply.</p>}
              </div>
              <div>
                <Label>Access Release Method *</Label>
                <select value={form.release_method} onChange={(e) => setForm({ ...form, release_method: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-sm" data-testid="release-method-select">
                  <option value="automatic">Automatic - Instant pass delivery</option>
                  <option value="manual">Manual Approval - Review before release</option>
                </select>
              </div>
            </div>
          </div>

          <SmartEntryFlowSection form={form} setForm={setForm} />

          <SourceOptionsSection
            value={form.source_options}
            onChange={(next) => setForm({ ...form, source_options: next })}
            onSaveAsDefault={async (current) => {
              try {
                const token = localStorage.getItem('session_token');
                const merged = { ...(savedHostDefaults || {}), source_options: current };
                await axios.put(`${API}/users/host-defaults`, merged, { headers: { Authorization: `Bearer ${token}` } });
                const resp = await axios.get(`${API}/users/host-defaults`, { headers: { Authorization: `Bearer ${token}` } });
                setSavedHostDefaults(resp.data || {});
                toast.success('Source options saved as default');
              } catch {
                toast.error('Could not save defaults');
              }
            }}
          />

          <TierSection
            form={form} savedPassTiers={savedPassTiers} showTierImport={showTierImport}
            tierImportSelection={tierImportSelection} setTierImportSelection={setTierImportSelection}
            openTierImport={openTierImport} setShowTierImport={setShowTierImport}
            addPassTier={addPassTier} removePassTier={removePassTier} updatePassTier={updatePassTier}
            isTierAlreadyImported={isTierAlreadyImported} importSelectedTiers={importSelectedTiers}
            navigate={navigate}
          />

          <PaymentLinksSection
            form={form} savedPaymentMethods={savedPaymentMethods} showPaymentImport={showPaymentImport}
            paymentImportSelection={paymentImportSelection} setPaymentImportSelection={setPaymentImportSelection}
            openPaymentImport={openPaymentImport} setShowPaymentImport={setShowPaymentImport}
            addPaymentLink={addPaymentLink} removePaymentLink={removePaymentLink} updatePaymentLink={updatePaymentLink}
            isPaymentAlreadyImported={isPaymentAlreadyImported} importSelectedPayments={importSelectedPayments}
            handlePaymentQRUpload={handlePaymentQRUpload} handleSavePaymentMethodDefault={handleSavePaymentMethodDefault}
            savingPaymentDefault={savingPaymentDefault}
          />

          <BrandingSection
            form={form} uploadingLogo={uploadingLogo} uploadingImage={uploadingImage}
            savingOrgLogo={savingOrgLogo} onFileUpload={handleFileUpload} onSaveOrgLogoDefault={handleSaveOrganizerLogoDefault}
          />

          <StaffAccessSection
            form={form} setForm={staffSectionSetForm} staffEmail={staffEmail} setStaffEmail={setStaffEmail}
            addingStaff={addingStaff} setAddingStaff={setAddingStaff} API={API}
            onSaveDefault={handleSaveStaffDefault}
          />

          {/* Validation Summary + Submit */}
          <div>
            <ValidationSummary formRef={formRef} triggerNonce={validationNonce} />
            <div className="flex justify-end space-x-4">
              <Button type="button" onClick={() => navigate('/dashboard')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm px-6 py-3" data-testid="cancel-button">Cancel</Button>
              <Button type="submit" className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold" disabled={loading} data-testid="submit-event-button">{loading ? 'Creating...' : 'Create Event'}</Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

